/** Account page: your name and password; for admins, the people who can sign in and invitations. */

import { ArrowLeft, Check, Copy, MailPlus, ShieldCheck, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api, type HealthState, type Invite, type User } from "./api";
import { NewPassword, passwordOk } from "./Login";

export function Account({ user, onUser }: { user: User; onUser: (u: User) => void }) {
  return (
    <div className="projects account">
      <header>
        <h1>Account</h1>
        <a className="button" href="#/"><ArrowLeft size={15} /> Projects</a>
      </header>
      <Profile user={user} onUser={onUser} />
      <Password />
      <ConnectedApps />
      {user.admin && <People me={user} />}
      {user.admin && <Health />}
      {user.admin && <Vocabulary />}
    </div>
  );
}

function Status({ ok, err }: { ok?: string; err?: string }) {
  if (err) return <p className="bad small">{err}</p>;
  return ok ? <p className="good small"><Check size={13} /> {ok}</p> : null;
}

function Profile({ user, onUser }: { user: User; onUser: (u: User) => void }) {
  const [name, setName] = useState(user.name);
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      onUser(await api.updateAccount(name));
      setMsg({ ok: "Saved." });
    } catch (x) {
      setMsg({ err: (x as Error).message });
    }
  };
  return (
    <section className="account-box">
      <h3>Profile</h3>
      <form onSubmit={save} className="account-form">
        <label>Email<input value={user.email} readOnly /></label>
        <label>Name<input value={name} onChange={(e) => setName(e.target.value)} /></label>
        <button className="primary" disabled={!name.trim() || name === user.name}>Save</button>
      </form>
      <Status {...msg} />
    </section>
  );
}

function Password() {
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [again, setAgain] = useState("");
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.changePassword(current, pw);
      setCurrent("");
      setPw("");
      setAgain("");
      setMsg({ ok: "Password changed. Other devices are signed out." });
    } catch (x) {
      setMsg({ err: (x as Error).message });
    }
  };
  return (
    <section className="account-box">
      <h3>Password</h3>
      <form onSubmit={save} className="account-form">
        <input type="password" autoComplete="current-password" placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        <NewPassword pw={pw} setPw={setPw} again={again} setAgain={setAgain} />
        <button className="primary" disabled={!current || !passwordOk(pw, again)}>Change password</button>
      </form>
      <Status {...msg} />
    </section>
  );
}

/** A link the server made; shown so it can be passed on by hand when email isn't set up or didn't arrive. */
function LinkBox({ label, link }: { label: string; link: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard needs HTTPS: leave the text selected instead
    }
  };
  return (
    <div className="link-box">
      <span className="small">{label}</span>
      <div className="share-url">
        <input readOnly value={link} onFocus={(e) => e.target.select()} />
        <button onClick={copy}>{copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}</button>
      </div>
    </div>
  );
}

function People({ me }: { me: User }) {
  const [data, setData] = useState<{ users: User[]; invites: Invite[]; email: boolean } | null>(null);
  const [email, setEmail] = useState("");
  const [admin, setAdmin] = useState(false);
  const [link, setLink] = useState<{ label: string; link: string } | null>(null);
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  const load = () => api.adminUsers().then(setData).catch((e) => setMsg({ err: e.message }));
  useEffect(() => void load(), []);

  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    setMsg({});
    try {
      await fn();
      if (ok) setMsg({ ok });
      await load();
    } catch (x) {
      setMsg({ err: (x as Error).message });
    }
  };
  const showLink = (who: string, what: string, r: { link: string; sent: boolean }) => {
    setLink({ label: r.sent ? `Emailed to ${who}. You can also send them this ${what} link yourself:` : `Email didn't go out. Send ${who} this ${what} link yourself:`, link: r.link });
  };
  const invite = (e: React.FormEvent) => {
    e.preventDefault();
    const to = email.trim();
    void act(async () => {
      showLink(to, "invitation", await api.invite(to, admin));
      setEmail("");
      setAdmin(false);
    });
  };
  const resend = (inv: Invite) => act(async () => showLink(inv.email, "invitation", await api.invite(inv.email, inv.admin)));

  return (
    <section className="account-box">
      <h3>People</h3>
      {data && !data.email && (
        <p className="warn small">Email isn't set up on the server, so invitations and resets aren't sent. Copy the links below and pass them on yourself.</p>
      )}
      <form onSubmit={invite} className="invite-form">
        <input type="email" placeholder="Invite by email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <label className="small"><input type="checkbox" checked={admin} onChange={(e) => setAdmin(e.target.checked)} /> Admin</label>
        <button className="primary" disabled={!email.trim()}><MailPlus size={15} /> Invite</button>
      </form>
      {link && <LinkBox {...link} />}
      <Status {...msg} />

      {data && data.invites.length > 0 && (
        <>
          <h4>Waiting to accept</h4>
          <table className="people">
            <tbody>
              {data.invites.map((i) => (
                <tr key={i.id}>
                  <td>{i.email}</td>
                  <td className="muted small">{i.admin ? "admin · " : ""}expires {new Date(i.expires * 1000).toLocaleDateString()}</td>
                  <td className="actions">
                    <button onClick={() => resend(i)}>Send again</button>
                    <button className="icon" title="Cancel the invitation" onClick={() => act(() => api.revokeInvite(i.id))}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h4>Accounts</h4>
      <table className="people">
        <tbody>
          {data?.users.map((u) => (
            <tr key={u.id} className={u.disabled ? "disabled" : ""}>
              <td>
                {u.name} {u.admin && <ShieldCheck size={13} className="muted" aria-label="admin" />}
                <div className="muted small">{u.email}</div>
              </td>
              <td className="muted small">
                {u.disabled ? "disabled" : u.last_login ? `last signed in ${new Date(u.last_login * 1000).toLocaleDateString()}` : "never signed in"}
              </td>
              <td className="actions">
                {u.id !== me.id && (
                  <>
                    {!u.disabled && <button onClick={() => act(async () => showLink(u.email, "password-reset", await api.sendReset(u.id)))}>Reset password</button>}
                    <button onClick={() => act(() => api.updateUser(u.id, { admin: !u.admin }))}>{u.admin ? "Remove admin" : "Make admin"}</button>
                    <button onClick={() => (u.disabled || confirm(`Disable ${u.email}? They're signed out and can't sign in until you enable them again.`)) && act(() => api.updateUser(u.id, { disabled: !u.disabled }))}>
                      {u.disabled ? "Enable" : "Disable"}
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** The shared word list the speech recognizer is told to expect, for every project. */
function Vocabulary() {
  const [text, setText] = useState<string | null>(null);
  const [saved, setSaved] = useState("");
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  useEffect(() => {
    api.vocabulary().then((r) => { setText(r.text); setSaved(r.text); }).catch((e) => setMsg({ err: e.message }));
  }, []);
  const save = async () => {
    setMsg({});
    try {
      const r = await api.saveVocabulary(text ?? "");
      setSaved(text ?? "");
      setMsg({ ok: `Saved ${r.terms} term${r.terms === 1 ? "" : "s"}.` });
    } catch (e) {
      setMsg({ err: (e as Error).message });
    }
  };
  return (
    <section className="account-box">
      <h3>Speech recognition words</h3>
      <p className="muted small">
        Names, acronyms and jargon said in voiceovers, one per line, spelled the way captions should show them (MXC, airworthiness, M&amp;E). Studio tells the
        recognizer to expect them when it finds cue phrases, writes captions or transcribes for a new voice. Each project can add its own in its settings.
        A line like <code>in rival =&gt; and RII</code> fixes a phrase the recognizer keeps mishearing, everywhere it occurs. Lines starting with # are
        notes. Regenerate a project's captions to apply changes.
      </p>
      <textarea className="vocab" rows={8} value={text ?? ""} disabled={text === null} onChange={(e) => setText(e.target.value)}
        placeholder={"airworthiness\nMXC\nM&E\nprevious texts => previous techs"} />
      <div className="row-buttons">
        <button className="primary" disabled={text === null || text === saved} onClick={save}>Save</button>
        {msg.ok && <span className="good small">{msg.ok}</span>}
        {msg.err && <span className="bad small">{msg.err}</span>}
      </div>
    </section>
  );
}

const ago = (t: number) => {
  const s = Math.max(0, Date.now() / 1000 - t);
  return s < 90 ? "just now" : s < 5400 ? `${Math.round(s / 60)} min ago` : s < 129600 ? `${Math.round(s / 3600)} h ago` : `${Math.round(s / 86400)} days ago`;
};

/** What the worker's monitor last saw; alerts go to every admin by email. */
function Health() {
  const [st, setSt] = useState<HealthState | null>(null);
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  useEffect(() => {
    const load = () => api.health().then(setSt).catch((e) => setMsg({ err: e.message }));
    void load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);
  const test = async () => {
    setMsg({});
    try {
      const r = await api.healthTest();
      setMsg(r.sent ? { ok: "Test alert sent to your email." } : { err: "Couldn't send: email isn't set up or the mail service refused it." });
    } catch (e) {
      setMsg({ err: (e as Error).message });
    }
  };
  const stale = st?.checked ? Date.now() / 1000 - st.checked > st.interval * 3 : true;
  const checks = Object.entries(st?.checks ?? {}).sort(([a], [b]) => a.localeCompare(b));
  return (
    <section className="account-box">
      <h3>Health</h3>
      {st && (
        <>
          <p className={stale ? "bad small" : "muted small"}>
            {st.checked ? `Checked ${ago(st.checked)}` : "Not checked yet"}
            {stale && " — the monitor isn't running (is the worker container up?)"}
            {!stale && ` · every ${st.interval / 60} min`}. Problems are emailed to {st.recipients.join(", ") || "nobody (no admins)"}
            {!st.email && " — but email isn't set up, so alerts only go to the server log"}.
            {st.ping ? " An outside service is pinged after each check, to catch the whole server going down." : ""}
          </p>
          <table className="health">
            <tbody>
              {checks.map(([name, c]) => (
                <tr key={name}>
                  <td>{c.ok ? <span className="good">●</span> : <span className="bad">●</span>}</td>
                  <td>{name}</td>
                  <td className="muted">{c.detail}{!c.ok && ` (since ${ago(c.since)})`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <div className="row-buttons">
        <button onClick={test}>Send a test alert</button>
        {msg.ok && <span className="good small">{msg.ok}</span>}
        {msg.err && <span className="bad small">{msg.err}</span>}
      </div>
    </section>
  );
}

/** Claude connector: the address to add in Claude, and the connections you've approved. */
function ConnectedApps() {
  const [apps, setApps] = useState<{ id: string; client_name: string; access: string; created: number; used: number | null }[] | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");
  const load = () => api.connectedApps().then(setApps).catch((e) => setErr(e.message));
  useEffect(() => void load(), []);
  const url = `${location.origin}/mcp`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* plain http: the field can still be selected */
    }
  };
  return (
    <section className="account-box">
      <h3>Connected apps</h3>
      <p className="muted small">
        Let Claude read your projects: in claude.ai go to Settings → Connectors → <strong>Add custom connector</strong>, name it Whiteboard Studio, and paste
        this address. Claude then opens a Studio page where you approve it. It sees only your projects; it can edit them only if you allow it (below or
        when approving), and every edit is saved as a version you can undo from History.
      </p>
      <div className="link-box">
        <input readOnly value={url} onFocus={(e) => e.target.select()} />
        <button onClick={copy}>{copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}</button>
      </div>
      {err && <p className="bad small">{err}</p>}
      {apps && apps.length > 0 && (
        <table className="health">
          <tbody>
            {apps.map((a) => (
              <tr key={a.id}>
                <td>{a.client_name}</td>
                <td>
                  <select value={a.access} onChange={(e) => api.setAppAccess(a.id, e.target.value as "read" | "edit").then(load)}>
                    <option value="read">Read-only</option>
                    <option value="edit">Can edit</option>
                  </select>
                </td>
                <td className="muted">connected {ago(a.created)}{a.used ? `, last used ${ago(a.used)}` : ""}</td>
                <td><button onClick={() => api.disconnectApp(a.id).then(load)}>Disconnect</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {apps?.length === 0 && <p className="muted small">Nothing connected yet.</p>}
    </section>
  );
}
